import React, { useMemo } from "react";
import {
  Box,
  VStack,
  Text,
  IconButton,
  Flex,
  Badge,
  HStack,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  AccordionIcon,
} from "@chakra-ui/react";
import { SelectedSupplier } from "./utils/partsCalculationUtils";
import { MdClose, MdLocationOn } from "react-icons/md";
import { formatDate, formatTime } from "../../utils/dateResolvers";

interface SupplierBucketListProps {
  selectedSuppliers: SelectedSupplier[];
  showOrderBadge?: boolean;
  onRemoveSupplier?: (supplierId: string) => void;
  originalParts?: Array<{
    rootPartNumber: string;
    partNumber: string;
    quantity: number;
  }>;
}

const SupplierBucketList: React.FC<SupplierBucketListProps> = ({
  selectedSuppliers,
  showOrderBadge = true,
  onRemoveSupplier,
  originalParts,
}) => {
  const isFiniteNumber = (v: any): v is number =>
    typeof v === "number" && Number.isFinite(v);

  const {
    calculateActualFulfillment,
    originalQuantityMap,
    rootToPartNumberMap,
  } = useMemo(() => {
    const fulfillmentMap = new Map<string, Map<string, number>>();
    const quantityMap = new Map<string, number>();
    const partNumberMap = new Map<string, string>();
    const hasOriginalParts =
      Array.isArray(originalParts) && originalParts.length > 0;

    if (!hasOriginalParts) {
      selectedSuppliers.forEach((supplier) => {
        const supplierFulfillment = new Map<string, number>();
        (supplier.selectedParts || []).forEach((part) => {
          const requested =
            typeof part.familyQuantity === "number" ? part.familyQuantity : 0;
          supplierFulfillment.set(part.rootPartNumber, requested);
        });
        fulfillmentMap.set(supplier.supplierId, supplierFulfillment);
      });
      return {
        calculateActualFulfillment: fulfillmentMap,
        originalQuantityMap: quantityMap,
        rootToPartNumberMap: partNumberMap,
      };
    }

    originalParts.forEach((part) => {
      quantityMap.set(part.rootPartNumber, part.quantity);
      if (part.partNumber) {
        partNumberMap.set(part.rootPartNumber, part.partNumber);
      }
    });

    const cumulativeFulfillment = new Map<string, number>();
    selectedSuppliers.forEach((supplier) => {
      const supplierFulfillment = new Map<string, number>();
      (supplier.selectedParts || []).forEach((part) => {
        const originalQty = quantityMap.get(part.rootPartNumber) ?? 0;
        const alreadyFulfilled =
          cumulativeFulfillment.get(part.rootPartNumber) ?? 0;
        const remainingNeeded = Math.max(0, originalQty - alreadyFulfilled);
        const requested =
          typeof part.familyQuantity === "number" ? part.familyQuantity : 0;
        const actualFulfilling = Math.min(requested, remainingNeeded);
        supplierFulfillment.set(part.rootPartNumber, actualFulfilling);
        cumulativeFulfillment.set(
          part.rootPartNumber,
          alreadyFulfilled + actualFulfilling
        );
      });
      fulfillmentMap.set(supplier.supplierId, supplierFulfillment);
    });
    return {
      calculateActualFulfillment: fulfillmentMap,
      originalQuantityMap: quantityMap,
      rootToPartNumberMap: partNumberMap,
    };
  }, [selectedSuppliers, originalParts]);

  return (
    <VStack spacing={3} align="stretch" width={"100%"} overflowX={"hidden"}>
      <Accordion allowMultiple defaultIndex={[]}>
        {selectedSuppliers.map((supplier, index) => {
          const actualFulfillment = calculateActualFulfillment.get(
            supplier.supplierId
          );

          return (
            <AccordionItem
              key={supplier.supplierId}
              border="none"
              mb={3}
              bg="#F0F6FF"
              borderRadius="lg"
              overflow="hidden"
            >
              <Box position="relative">
                {onRemoveSupplier && (
                  <IconButton
                    aria-label="Remove supplier"
                    icon={<MdClose size="15" />}
                    variant="ghost"
                    colorScheme="red"
                    position="absolute"
                    top={2}
                    right={2}
                    zIndex={2}
                    size="xs"
                    onClick={() => onRemoveSupplier(supplier.supplierId)}
                    _hover={{ bg: "red.100" }}
                  />
                )}

                <AccordionButton
                  px={4}
                  py={4}
                  _hover={{ bg: "#E0EDFF" }}
                  _expanded={{ bg: "#E0EDFF" }}
                >
                  <Flex
                    justify="space-between"
                    align="center"
                    width="100%"
                    pr={8}
                  >
                    <VStack align="start" spacing={1} flex={1}>
                      <HStack spacing={2} align="center">
                        {showOrderBadge && (
                          <Badge
                            bg="transparent"
                            color="#3B82F6"
                            borderRadius="full"
                            fontSize="0.75rem"
                            fontWeight="bold"
                            fontFamily="'Nunito','Inter', sans-serif"
                            minW="24px"
                          >
                            #{index + 1}
                          </Badge>
                        )}
                        <Text
                          fontSize="0.9rem"
                          fontWeight="bold"
                          color="gray.800"
                          fontFamily="'Nunito','Inter', sans-serif"
                        >
                          {supplier.supplierName}
                        </Text>
                      </HStack>
                      <Flex
                        align="center"
                        color="gray.500"
                        gap={1}
                        fontFamily="'Nunito','Inter', sans-serif"
                      >
                        <MdLocationOn
                          size="0.9rem"
                          color={
                            isFiniteNumber(supplier.distanceKm)
                              ? "#3b82f6"
                              : "#E40133"
                          }
                        />
                        <Text
                          fontSize="0.7rem"
                          color="gray.600"
                          fontFamily="'Nunito','Inter', sans-serif"
                        >
                          {isFiniteNumber(supplier.distanceKm)
                            ? `${supplier.distanceKm.toFixed(1)} km away`
                            : "Unavailable"}
                        </Text>
                      </Flex>
                    </VStack>
                    <AccordionIcon color="gray.600" />
                  </Flex>
                </AccordionButton>

                {supplier.selectedParts &&
                  supplier.selectedParts.length > 0 && (
                    <AccordionPanel px={4} pb={4} pt={0}>
                      <Box
                        bg="white"
                        borderRadius="md"
                        p={3}
                        border="1px solid"
                        borderColor="gray.200"
                      >
                        <Text
                          fontSize="0.7rem"
                          fontWeight="700"
                          color="gray.600"
                          textTransform="uppercase"
                          letterSpacing="0.4px"
                          mb={1}
                          fontFamily="'Nunito','Inter', sans-serif"
                        >
                          Parts Fulfilled
                        </Text>
                        <Box height="2px" width="100%" bg="#e2e8f0" mb={2} />
                        <VStack align="stretch" spacing={2}>
                          {supplier.selectedParts.map((part, partIndex) => {
                            const originalQty =
                              originalQuantityMap.get(part.rootPartNumber) || 0;
                            const actualFulfilling =
                              actualFulfillment?.get(part.rootPartNumber) || 0;
                            const partNumber =
                              rootToPartNumberMap.get(part.rootPartNumber)

                            return (
                              <Box
                                key={`${part.rootPartNumber}-${partIndex}`}
                                pb={2}
                                borderBottom={
                                  partIndex < supplier.selectedParts.length - 1
                                    ? "1px solid"
                                    : "none"
                                }
                                borderColor="gray.100"
                              >
                                <HStack
                                  justify="space-between"
                                  align="baseline"
                                  fontSize="0.8rem"
                                  fontFamily="'Nunito','Inter', sans-serif"
                                >
                                  <Box flex={1} minW={0}>
                                    <Text
                                      color="gray.700"
                                      fontWeight="600"
                                      wordBreak="break-word"
                                    >
                                      {partNumber}
                                    </Text>
                                    {part.updatedAt && (
                                      <Text
                                        color="gray.700"
                                        fontSize="0.65rem"
                                        fontWeight="500"
                                      >
                                        Updated at:{" "}
                                        {`${formatDate(
                                          part.updatedAt
                                        )}, ${formatTime(part.updatedAt)}`}
                                      </Text>
                                    )}
                                  </Box>
                                  <HStack spacing={1.5} flexShrink={0}>
                                    <Text color="#3B82F6" fontWeight="700">
                                      {actualFulfilling}
                                    </Text>
                                    {originalQty > 0 && (
                                      <>
                                        <Text
                                          color="gray.400"
                                          fontSize="0.7rem"
                                        >
                                          /
                                        </Text>
                                        <Text
                                          color="gray.500"
                                          fontSize="0.7rem"
                                        >
                                          {originalQty}
                                        </Text>
                                      </>
                                    )}
                                  </HStack>
                                </HStack>
                              </Box>
                            );
                          })}
                        </VStack>
                      </Box>
                    </AccordionPanel>
                  )}
              </Box>
            </AccordionItem>
          );
        })}
      </Accordion>
    </VStack>
  );
};

export default SupplierBucketList;
